import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { captureMorphFocus, restoreMorphFocus } from '@theme/cart-refresh';

/**
 * Wraps cart content (drawer or page) and self-morphs
 * when cart:update events carry section HTML for this component.
 *
 * Attributes:
 * - data-section-id: the section ID to match in event.detail.data.sections
 * - data-drawer: if present, this is the cart drawer instance
 */
export class CartItemsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    /* Don't react to events dispatched by ourselves */
    if (event.target === this) return;

    const sections = event.detail?.data?.sections;
    const sectionId = this.dataset.sectionId;

    /* If the event carries our section HTML, replace directly */
    if (sections && sectionId && sections[sectionId]) {
      this.#replaceContent(sections[sectionId]);
      return;
    }

    /* Fallback: fetch the section from the server.
       Invalidate stale page-load cache first, then force a fresh fetch
       so we don't morph back to an empty cart. */
    if (sectionId) {
      sectionRenderer.invalidate(sectionId);
      sectionRenderer.renderSection(sectionId, { cache: false }).then((html) => {
        this.#replaceContent(html);
      }).catch((err) => {
        console.error('[cart-items-component] render failed:', err);
      });
    }
  };

  /**
   * Replaces this component's inner content with the cart-items-component
   * extracted from the given section HTML.
   */
  #replaceContent(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newContent = doc.querySelector('cart-items-component');
    if (!newContent) return;
    /* The innerHTML wipe destroys the focused control synchronously — e.g.
       the qty button just clicked, or the close button focused by the
       drawer's auto-open. Capture it before and restore the equivalent
       control (or the drawer anchor) right after, in the same task: a
       rAF-based restore would run once the node is already detached. */
    const previousFocus = captureMorphFocus(this);
    this.innerHTML = newContent.innerHTML;
    restoreMorphFocus(this, previousFocus);
  }

  get isDrawer() {
    return this.hasAttribute('data-drawer');
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
